import React, { useEffect, useState } from "react";
import { ExternalLink, RefreshCw, Loader2, Unlink, Eye, Heart, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useLang } from "@/context/LangContext";

const n = (v) => Number(v || 0).toLocaleString("en-US");

/** حساب مرتبط: الملف والإحصاءات (user.info.basic/stats) + آخر الفيديوهات (video.list) + مزامنة وفصل */
export const ConnectedAccount = ({ acc, onDisconnect, onSynced }) => {
  const { t } = useLang();
  const [media, setMedia] = useState([]);
  const [open, setOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  useEffect(() => { api.get(`/social/accounts/${acc.id}/media`).then((r) => setMedia(r.data || [])).catch(() => {}); }, [acc.id, acc.synced_at]);
  const sync = async () => {
    setSyncing(true);
    try { await api.post(`/social/accounts/${acc.id}/sync`); toast.success(t("تمت مزامنة الحساب", "Account synced")); onSynced?.(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSyncing(false); }
  };
  return (
    <div className="space-y-2" data-testid={`connected-${acc.platform}`}>
      <div className="flex items-center gap-2">
        {acc.avatar ? <img src={acc.avatar} alt="" className="w-9 h-9 rounded-full border border-border object-cover" data-testid={`account-avatar-${acc.platform}`} /> : <div className="w-9 h-9 rounded-full bg-secondary" />}
        <div className="min-w-0">
          <div className="text-xs font-bold truncate" data-testid={`account-name-${acc.platform}`}>{acc.username || acc.external_id}</div>
          {acc.profile_url && <a href={acc.profile_url} target="_blank" rel="noreferrer" className="text-[11px] text-primary inline-flex items-center gap-1" data-testid={`account-profile-link-${acc.platform}`}>{t("فتح الملف", "Open profile")} <ExternalLink className="w-3 h-3" /></a>}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-1 text-center text-[11px]" data-testid={`account-stats-${acc.platform}`}>
        <div className="rounded-lg bg-secondary/60 py-1"><b className="block text-sm">{n(acc.followers)}</b>{t("متابع", "Followers")}</div>
        <div className="rounded-lg bg-secondary/60 py-1"><b className="block text-sm">{n(acc.likes)}</b>{t("إعجاب", "Likes")}</div>
        <div className="rounded-lg bg-secondary/60 py-1"><b className="block text-sm">{n(acc.posts_count)}</b>{t("منشور", "Posts")}</div>
      </div>
      {media.length > 0 && (
        <div>
          <button type="button" className="text-[11px] text-primary underline underline-offset-2" onClick={() => setOpen(!open)} data-testid={`toggle-media-${acc.platform}`}>{open ? t("إخفاء الفيديوهات", "Hide videos") : t(`آخر ${media.length} فيديو/منشور`, `Latest ${media.length} videos`)}</button>
          {open && (
            <ul className="mt-1 space-y-1 max-h-56 overflow-auto" data-testid={`media-list-${acc.platform}`}>
              {media.map((m) => (
                <li key={m.media_id} className="flex items-center gap-2 text-[11px] rounded-lg border border-border p-1.5">
                  {m.cover && <img src={m.cover} alt="" className="w-8 h-11 rounded object-cover" />}
                  <div className="min-w-0 flex-1">
                    <div className="truncate">{m.caption || m.date}</div>
                    <div className="text-muted-foreground flex items-center gap-2"><span className="inline-flex items-center gap-0.5"><Eye className="w-3 h-3" />{n(m.views)}</span><span className="inline-flex items-center gap-0.5"><Heart className="w-3 h-3" />{n(m.likes)}</span><span className="inline-flex items-center gap-0.5"><MessageCircle className="w-3 h-3" />{n(m.comments)}</span></div>
                  </div>
                  {m.permalink && <a href={m.permalink} target="_blank" rel="noreferrer" className="text-primary"><ExternalLink className="w-3 h-3" /></a>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {acc.platform === "tiktok" && acc.scopes !== undefined && !(acc.scopes || "").includes("video.publish") && (
        <div className="text-[11px] rounded-lg border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300 px-2 py-1.5" data-testid="tiktok-reauth-hint">
          {t("لتفعيل النشر التلقائي على تيك توك: افصل الحساب ثم أعد ربطه لمنح صلاحية النشر.", "To enable auto-publishing on TikTok: disconnect and reconnect the account to grant the publish permission.")}
        </div>
      )}
      <div className="flex gap-1 mt-auto">
        <Button size="sm" variant="outline" className="gap-1 flex-1" onClick={sync} disabled={syncing} data-testid={`sync-${acc.platform}`}>{syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} {t("مزامنة", "Sync")}</Button>
        <Button size="sm" variant="outline" className="gap-1 border-destructive/40 text-destructive" onClick={onDisconnect} data-testid={`disconnect-${acc.platform}`}><Unlink className="w-3.5 h-3.5" /> {t("فصل", "Disconnect")}</Button>
      </div>
    </div>
  );
};
